// TeamScreen — director's team list (docs/miniapp-m3-tz.md §3.5).
// Per-manager card: avatar + live status dot, current load (open/overdue/score),
// a 14-day score sparkline and a WoW overdue delta-chip. Tap → manager detail.
// Directors can also add/remove LOCAL employees (Telegram-only, no Bitrix
// account) and copy their one-time invite link to send in Telegram.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { addLocalMember, fetchTeam, removeLocalMember, updateLocalMember } from '../../api/director'
import type { LocalMemberInviteResponse, Role, TeamMemberCard } from '../../api/types'
import { Avatar, Button, EmptyState, Icon, ScreenSkeleton, Sparkline, t, useToast } from '../../design'
import { getWebApp, hapticNotify } from '../../telegram/tg'

// Telegram-native confirm with a web fallback (same pattern as TaskScreen).
function confirmRemove(message: string): Promise<boolean> {
  const wa = getWebApp() as { showConfirm?: (m: string, cb: (ok: boolean) => void) => void } | null
  if (wa?.showConfirm) return new Promise((res) => wa.showConfirm!(message, (ok) => res(!!ok)))
  return Promise.resolve(typeof window === 'undefined' ? true : window.confirm(message))
}

function DeltaChip({ delta, good }: { delta: number; good: boolean }) {
  if (!delta) return null
  const color = good ? 'var(--success)' : 'var(--danger)'
  const bg = good ? 'var(--success-bg)' : 'var(--danger-bg)'
  const Arrow = delta > 0 ? Icon.ArrowUp : Icon.ArrowDown
  return (
    <span className="t-caption-s" style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color, background: bg, borderRadius: 100, padding: '1px 6px' }}>
      <Arrow size={11} sw={2.75} />{Math.abs(delta)}
    </span>
  )
}

function Stat({ n, label, danger }: { n: number | string; label: string; danger?: boolean }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div className="t-body-s" style={{ fontVariantNumeric: 'tabular-nums', color: danger && n ? 'var(--danger)' : undefined }}>{n}</div>
      <div className="t-caption" style={{ fontSize: 11 }}>{label}</div>
    </div>
  )
}

function ManagerCard({ m, onClick, onRemove, onEdit }: { m: TeamMemberCard; onClick: () => void; onRemove?: () => void; onEdit?: () => void }) {
  const name = m.name || m.short_name
  // A local member with no presence + no open tasks hasn't opened the invite yet.
  const notConnected = m.local && m.status === 'offline' && m.stats.open_tasks === 0
  const statusText = notConnected ? t('team.notConnected')
    : m.inactive_this_window ? t('team.inactive')
    : m.status === 'active' ? t('team.active') : t('team.offline')
  return (
    <div className="card card-16" onClick={onClick} style={{ opacity: m.inactive_this_window ? 0.55 : 1, cursor: 'pointer' }}>
      <div className="row middle gap-3" style={{ marginBottom: 10 }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Avatar name={name} size={40} />
          <span style={{ position: 'absolute', right: -1, bottom: -1, width: 11, height: 11, borderRadius: '50%', background: m.status === 'active' ? 'var(--success)' : 'var(--text-3)', border: '2px solid var(--surface)' }} />
        </div>
        <div className="flex-1" style={{ minWidth: 0 }}>
          <div className="row middle gap-2" style={{ minWidth: 0 }}>
            <span className="t-body-s ellipsis">{name}</span>
            {m.local && (
              <span className="t-caption-s" style={{ flexShrink: 0, color: 'var(--text-3)', background: 'var(--surface-2)', borderRadius: 100, padding: '1px 7px' }}>{t('team.localBadge')}</span>
            )}
            {m.local && m.role && (
              <span className="t-caption-s" style={{ flexShrink: 0, color: 'var(--brand-600)', background: 'var(--brand-50)', borderRadius: 100, padding: '1px 7px' }}>
                {m.role === 'director' ? t('role.director') : t('role.manager')}
              </span>
            )}
          </div>
          <div className="t-caption">{statusText}</div>
        </div>
        {m.local && onEdit && (
          <button
            className="t-caption"
            onClick={(e) => { e.stopPropagation(); onEdit() }}
            aria-label={t('team.edit')}
            style={{ flexShrink: 0, background: 'none', border: 'none', padding: 6, color: 'var(--text-3)', cursor: 'pointer', display: 'flex' }}>
            <Icon.Settings size={16} sw={2} />
          </button>
        )}
        {m.local && onRemove && (
          <button
            className="t-caption"
            onClick={(e) => { e.stopPropagation(); onRemove() }}
            aria-label={t('team.remove')}
            style={{ flexShrink: 0, background: 'none', border: 'none', padding: 6, color: 'var(--text-3)', cursor: 'pointer', display: 'flex' }}>
            <Icon.Trash size={16} sw={2} />
          </button>
        )}
        {m.trend.length > 0 && (
          // Fixed 70px box: the global `.spark { width: 100% }` makes the SVG fill its
          // flex parent, which otherwise collapses the name column to 0 width.
          <div style={{ width: 70, flexShrink: 0 }}>
            <Sparkline data={m.trend.map((p) => p.value ?? 0)} width={70} height={24} color="var(--brand-500)" />
          </div>
        )}
      </div>
      <div className="row middle gap-3">
        <Stat n={m.stats.open_tasks} label={t('team.open')} />
        <Stat n={m.stats.overdue} label={t('team.overdue')} danger />
        {m.stats.avg_score != null && <Stat n={m.stats.avg_score.toFixed(1)} label={t('dir.avgScore')} />}
        <span className="flex-1" />
        <DeltaChip delta={m.delta.overdue} good={m.delta.overdue < 0} />
      </div>
    </div>
  )
}

// Inline «Добавить сотрудника» form: name → invite link with a copy button.
function AddLocalForm() {
  const qc = useQueryClient()
  const toast = useToast()
  const [name, setName] = useState('')
  const [invite, setInvite] = useState<LocalMemberInviteResponse | null>(null)
  const [copied, setCopied] = useState(false)

  const addMut = useMutation({
    mutationFn: (n: string) => addLocalMember(n),
    onSuccess: (res) => {
      hapticNotify('success')
      setInvite(res)
      setName('')
      qc.invalidateQueries({ queryKey: ['team'] })
    },
    onError: () => { hapticNotify('error'); toast(t('toast.error')) },
  })

  const trimmed = name.trim()
  const canSubmit = trimmed.length > 0 && trimmed.length <= 100 && !addMut.isPending

  const copyLink = async () => {
    if (!invite) return
    try {
      await navigator.clipboard.writeText(invite.invite_url)
      setCopied(true)
      hapticNotify('success')
      setTimeout(() => setCopied(false), 1800)
    } catch {
      toast(t('toast.error'))
    }
  }

  return (
    <div className="card card-16" style={{ marginBottom: 12 }}>
      <div className="row gap-2">
        <input className="input flex-1" value={name} maxLength={100}
          onChange={(e) => setName(e.target.value)} placeholder={t('team.addLocalName')}
          onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) addMut.mutate(trimmed) }} />
        <Button variant="primary" icon={<Icon.Plus size={17} sw={2.5} />}
          onClick={() => canSubmit && addMut.mutate(trimmed)}
          style={{ opacity: canSubmit ? 1 : 0.5, pointerEvents: canSubmit ? 'auto' : 'none' }}>
          {t('team.addLocal')}
        </Button>
      </div>
      {invite && (
        <div className="col gap-2" style={{ marginTop: 12 }}>
          <div className="t-caption">{t('team.inviteReady')}</div>
          <div className="t-body-s" style={{ wordBreak: 'break-all', background: 'var(--surface-2)', borderRadius: 8, padding: '8px 10px' }}>{invite.invite_url}</div>
          <Button variant="secondary" full icon={<Icon.Link size={16} sw={2} />} onClick={copyLink}>
            {copied ? t('team.copied') : t('team.copyLink')}
          </Button>
        </div>
      )}
    </div>
  )
}

// Inline editor for a local member: role (manager/director) + Telegram id.
function LocalMemberEditor({ member, onClose }: { member: TeamMemberCard; onClose: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [role, setRole] = useState<Role>((member.role as Role) || 'manager')
  const [tg, setTg] = useState(member.telegram_chat_id || '')

  const saveMut = useMutation({
    mutationFn: () => updateLocalMember(member.id, { role, telegram_chat_id: tg.trim() }),
    onSuccess: () => {
      hapticNotify('success')
      toast(t('team.saved'))
      qc.invalidateQueries({ queryKey: ['team'] })
      onClose()
    },
    onError: () => { hapticNotify('error'); toast(t('toast.error')) },
  })

  // Telegram id is optional; when present it must be digits (a leading - is
  // allowed for group/channel ids). The backend validates too.
  const tgValid = tg.trim() === '' || /^-?\d+$/.test(tg.trim())
  const canSave = tgValid && !saveMut.isPending

  return (
    <div className="card card-16" style={{ border: '1px solid var(--brand-300)' }}>
      <div className="col gap-3">
        <div>
          <div className="t-caption" style={{ marginBottom: 6 }}>{t('sec.role')}</div>
          <div className="row gap-2">
            <Button variant={role === 'manager' ? 'primary' : 'secondary'} full onClick={() => setRole('manager')}>{t('role.manager')}</Button>
            <Button variant={role === 'director' ? 'primary' : 'secondary'} full onClick={() => setRole('director')}>{t('role.director')}</Button>
          </div>
        </div>
        <div>
          <div className="row middle gap-2" style={{ marginBottom: 6 }}>
            <Icon.Telegram size={14} sw={2} />
            <span className="t-caption">{t('team.telegramId')}</span>
          </div>
          <input className="input" value={tg} inputMode="numeric" maxLength={20}
            onChange={(e) => setTg(e.target.value)} placeholder={t('team.telegramHint')} />
        </div>
        <div className="row gap-2">
          <Button variant="secondary" full onClick={onClose}>{t('btn.cancel')}</Button>
          <Button variant="primary" full icon={<Icon.Check size={16} sw={2.5} />}
            onClick={() => canSave && saveMut.mutate()}
            style={{ opacity: canSave ? 1 : 0.5, pointerEvents: canSave ? 'auto' : 'none' }}>
            {t('btn.save')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export function TeamScreen() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()
  const [editingId, setEditingId] = useState<number | null>(null)
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['team'],
    queryFn: fetchTeam,
  })

  const removeMut = useMutation({
    mutationFn: (memberId: number) => removeLocalMember(memberId),
    onSuccess: () => {
      hapticNotify('success')
      qc.invalidateQueries({ queryKey: ['team'] })
    },
    onError: () => { hapticNotify('error'); toast(t('toast.error')) },
  })

  const doRemove = async (memberId: number) => {
    if (await confirmRemove(t('team.removeConfirm'))) removeMut.mutate(memberId)
  }

  if (isLoading) return <ScreenSkeleton kind="today" />

  if (isError || !data) {
    return (
      <div className="scroll-y" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', gap: 14, padding: 24 }}>
        <span style={{ width: 72, height: 72, borderRadius: 18, background: 'var(--surface-2)', color: 'var(--text-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon.CloudOff size={30} sw={2} />
        </span>
        <div className="t-h2">{t('task.loadError')}</div>
        <Button variant="secondary" icon={<Icon.Refresh size={16} sw={2} />} onClick={() => refetch()}>{t('btn.retry')}</Button>
      </div>
    )
  }

  return (
    <div className="scroll-y" style={{ flex: 1, padding: '12px 16px 24px' }}>
      <div className="t-display" style={{ marginBottom: 12 }}>{t('nav.team')}</div>
      <AddLocalForm />
      {data.managers.length === 0 ? (
        <EmptyState icon={<Icon.Users size={28} />} title={t('team.empty')} />
      ) : (
        <div className="col gap-2">
          {data.managers.map((m) => (
            <div key={m.id} className="col gap-2">
              <ManagerCard
                m={m}
                onClick={() => navigate(`/team/${m.id}`)}
                onRemove={() => doRemove(m.id)}
                onEdit={() => setEditingId(editingId === m.id ? null : m.id)}
              />
              {editingId === m.id && (
                <LocalMemberEditor member={m} onClose={() => setEditingId(null)} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
